import api from 'config/api.config';
import type { AxiosResponse } from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from 'utils/errors';

export const getAccountsByUserId = async (): Promise<AxiosResponse<any>> => {
  try {
    const response = await api.get('/api/accounts/my-accounts');
    return response;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to fetch accounts');
    toast.error(message);
    const err = error as { response?: AxiosResponse<any> };
    if (err.response) {
      return err.response;
    }
    throw new Error(message);
  }
};

export const getActiveAccountsByUserId = async (): Promise<AxiosResponse<any>> => {
  try {
    const response = await api.get('/api/accounts/my-active-accounts');
    return response;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to fetch active accounts');
    toast.error(message);
    const err = error as { response?: AxiosResponse<any> };
    if (err.response) {
      return err.response;
    }
    throw new Error(message);
  }
};

export const addOrModifyRule = async (social_media_account_id: string, rule_name: string, rule: unknown) => {
  try {
    const response = await api.post('/api/accounts/manage-rule', {
      social_media_account_id,
      rule_name,
      rule,
    });
    return response.data;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to manage rule');
    toast.error(message);
    const err = error as { response?: { data?: unknown } };
    return err.response ? err.response.data : error;
  }
};

export const fetchRules = async (social_media_account_id: string) => {
  try {
    const response = await api.get(`/api/accounts/rules?social_media_account_id=${social_media_account_id}`);
    return response.data;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to fetch rules');
    toast.error(message);
    const err = error as { response?: { data?: unknown } };
    return err.response ? err.response.data : error;
  }
};

export const performRuleAction = async (social_media_account_id: string, rule_name: string, status: unknown, is_active: boolean) => {
  try {
    const response = await api.post('/api/accounts/rule-action', {
      social_media_account_id,
      rule_name,
      status,
      is_active,
    });
    return response.data;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to perform rule action');
    toast.error(message);
    const err = error as { response?: { data?: unknown } };
    return err.response ? err.response.data : error;
  }
};

export const fetchAccountSettings = async (account_id: string) => {
  try {
    const response = await api.post('/api/accounts/social-account-global-settings', { account_id });
    return response.data;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to fetch account settings');
    toast.error(message);
    const err = error as { response?: { data?: unknown } };
    return err.response ? err.response.data : error;
  }
};

export const toggleAccountDeletedStatus = async (account_id: string, is_deleted: boolean) => {
  try {
    const response = await api.post('/api/accounts/delete-account', { account_id, is_deleted });
    return response.data;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to toggle account deleted status');
    toast.error(message);
    const err = error as { response?: { data?: unknown } };
    throw err.response ? err.response.data : error;
  }
};

export const toggleCategoryActiveStatus = async (account_id: string, category: string, is_active: boolean) => {
  try {
    const response = await api.post('/api/accounts/manage-category', { account_id, category, is_active });
    return response.data;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to toggle category active status');
    toast.error(message);
    const err = error as { response?: { data?: unknown } };
    throw err.response ? err.response.data : error;
  }
};

export const toggleAccountActiveStatus = async (account_id: string, is_active: boolean) => {
  try {
    const response = await api.post('/api/accounts/manage-account', { account_id, is_active });
    return response.data;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to toggle account active status');
    toast.error(message);
    const err = error as { response?: { data?: unknown } };
    throw err.response ? err.response.data : error;
  }
};

export const deleteRule = async (ruleId: string) => {
  try {
    const response = await api.delete(`/api/accounts/rule/${ruleId}`);
    return response.data;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to delete rule');
    toast.error(message);
    throw error;
  }
};

export const renameRule = async (ruleId: string, ruleName: string) => {
  try {
    const response = await api.put(`/api/accounts/rule/${ruleId}`, { rule_name: ruleName });
    return response.data;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to rename rule');
    toast.error(message);
    throw error;
  }
};

export const updateTimezone = async (accountId: string, timezoneOffset: number) => {
  try {
    const response = await api.post('/api/accounts/update-timezone', {
      account_id: accountId,
      timezone_offset: timezoneOffset,
    });
    return response.data;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to update timezone');
    toast.error(message);
    throw error;
  }
};

export const getAccountsWithRules = async (): Promise<AxiosResponse<any>> => {
  try {
    const response = await api.get('/api/accounts/accounts-with-rules');
    return response;
  } catch (error: unknown) {
    const message = getErrorMessage(error, 'Failed to fetch accounts with rules');
    toast.error(message);
    const err = error as { response?: AxiosResponse<any> };
    if (err.response) {
      return err.response;
    }
    throw new Error(message);
  }
};
